// Palworld TCG investing / collecting keyword clusters (September 2026).
//
// Modelled on the mature Pokemon investing + collecting query set (error and
// misprint value, 1st-edition sealed, Japanese-vs-English print differences,
// price tracking, promo values, upgrade paths) and mapped onto the Palworld
// Official Card Game equivalents so the pages exist before those queries
// develop real volume.
//
// Every claim is a checkable process claim (how to verify a printing, where to
// read live prices, how grading works). No price levels, print-run sizes or
// future-value predictions are asserted.

import type { Guide, GuideArticle } from "./guides-data";
import { OFFICIAL_LINKS } from "./official-links";

interface Topic {
  slug: string;
  title: string;
  desc: string;
  keywords: string;
  hub: string;
  hubLabel: string;
  clusterTitle: string;
  question: string;
  answer: string;
  steps: [string, string][];
  data: [string, string][];
  pitfalls: [string, string][];
  faq: [string, string][];
}

const TOPICS: Topic[] = [
  {
    slug: "palworld-tcg-error-cards",
    title: "Palworld TCG Error & Misprint Cards",
    desc:
      "How to identify, verify and value Palworld TCG error cards and misprints: miscut and off-centre packs, ink and colour faults, wrong-back and holo-bleed cards, error booster boxes, and how the Dawn of Palpagos reprint changes what counts as scarce.",
    keywords:
      "palworld tcg error box misprint value, palworld tcg error card, palworld tcg misprint value, palworld miscut card, palworld card off centre, palworld tcg printing error, palworld error booster box, palworld misprint booster pack, dawn of palpagos misprint",
    hub: "/prices",
    hubLabel: "Palworld TCG price chart",
    clusterTitle: "Palworld TCG error & misprint values",
    question: "Are Palworld TCG error cards and misprints worth anything?",
    answer:
      "Some are. Value in error cards comes from being verifiable, dramatic and repeatable-to-photograph — a full miscut, a wrong-back, a missing colour layer or a crimp through the art — not from ordinary off-centring, which nearly every print run produces. Because the first Palworld set, Booster Box \"Dawn of Palpagos\" (EBP01), is being reprinted after selling out, launch-run errors are the ones collectors can actually date, so document the pack, the printed card code and the fault under raking light before you list or buy anything.",
    steps: [
      [
        "Classify the fault before you price it",
        "Miscut (art shifted so a neighbouring card shows), wrong-back, missing ink layer, double print, crimp, holo-bleed and inverted foil are collectible categories. Light off-centring, a soft corner or a factory scratch are condition issues, not errors.",
      ],
      [
        "Photograph it the way a grader would",
        "Flat, indirect light for the front and back, then one raking-light shot across the surface to show the fault is in the print and not in handling. Include the printed English code (for example EBP01-001) in frame.",
      ],
      [
        "Prove it came from a sealed pack",
        "Keep the wrapper, and where possible film the pull. Unverified errors are treated as altered cards, and altered cards are unsellable at any price.",
      ],
      [
        "Check whether the fault repeats across the run",
        "A fault that appears on many copies is a print-run characteristic and prices like a normal card. A fault nobody else can show is what carries a premium. Community pull threads and the card database at /cards are how you check.",
      ],
      [
        "Price from completed sales of comparable errors",
        "There is no error price guide for a set this young. Read the median for the base printing on /prices, then treat any error premium as speculative and negotiated, not fixed.",
      ],
      [
        "Grade only a strong, dramatic error",
        "Graders label qualifying print errors, which is what makes a premium defensible to a buyer. On a subtle fault the fee outruns the uplift.",
      ],
    ],
    data: [
      ["Set most errors will come from", "Booster Box \"Dawn of Palpagos\" (EBP01), the first Palworld Official Card Game set, launched 30 July 2026."],
      ["Why the reprint matters", "Additional print runs were confirmed after the launch wave sold out, so a documented launch-window error is easier to date than a later one."],
      ["Box and pack maths", "12 packs per box, 7 cards per pack — 84 cards per box, which is the denominator for any 'one error per box' claim."],
      ["Codes to quote", "Always the exact printed English code with the leading E: EBP01-001, ETD01-012TSP, ESOUL-002."],
      ["Collectible error types", "Miscut, wrong-back, missing colour layer, double print, crimp, holo-bleed, inverted or missing foil."],
      ["Not errors", "Mild off-centring, factory surface lines, soft corners, edge whitening — these are graded as condition."],
      ["Where to read live prices", "The price chart at /prices for the base printing, and /tcg-price-index for how that printing is trending."],
    ],
    pitfalls: [
      [
        "Calling normal off-centring a misprint",
        "Centring varies across every print run in every card game. Buyers know this, so an off-centre listing sold as an error kills your credibility on the next sale.",
      ],
      [
        "Losing the wrapper",
        "Without pack provenance an error reads as tampering. The wrapper and a pull video are the cheapest value protection you have.",
      ],
      [
        "Assuming an error box exists because a pack was odd",
        "A genuine error box means the fault repeats through the box. One strange pack is one pack — open the rest and document before you use the phrase.",
      ],
      [
        "Anchoring to a Pokemon error price",
        "Mature-market error premiums come from decades of collector demand behind a known population. A 2026 Palworld error has neither yet.",
      ],
      [
        "Cleaning, flattening or trimming the fault",
        "Any alteration makes the card ungradeable and unsellable. Leave it exactly as pulled.",
      ],
    ],
    faq: [
      [
        "What is a Palworld TCG error card worth?",
        "There is no fixed figure — the set is too young for an error price guide. Value tracks how dramatic and verifiable the fault is, so start from the base printing's median on /prices and treat any premium as negotiated between you and one buyer.",
      ],
      [
        "How do I know if my Palworld card is a real misprint?",
        "Check that the fault is in the print layer, not in handling: a shifted cut line, a missing colour, a doubled image or a crimp. Photograph it under raking light with the printed code visible, and keep the wrapper as proof it came from a sealed pack.",
      ],
      [
        "Is a Palworld error booster box valuable?",
        "Only if the fault repeats through the box, which is what makes it a box-level error rather than one odd pack. Document every pack before describing it that way, because buyers will ask for exactly that evidence.",
      ],
      [
        "Does the Dawn of Palpagos reprint affect misprint values?",
        "It affects dating more than value. With extra print runs confirmed, buyers care whether an error came from the launch window, so pack provenance and purchase records matter more than they would in a single-run set.",
      ],
      [
        "Should I grade a Palworld misprint?",
        "Grade a dramatic, clearly documented error, because graders note qualifying print errors on the label and that is what makes a premium defensible. Skip grading for subtle faults, where the fee exceeds any realistic uplift.",
      ],
      [
        "Are miscut Palworld cards tournament legal?",
        "Treat them as collection pieces. A card that does not present as a standard printing invites a judge call, so play a normal copy and keep the miscut sleeved and out of the deck.",
      ],
    ],
  },
  {
    slug: "palworld-soul-card-upgrades",
    title: "Palworld Soul Card Upgrades",
    desc:
      "How Palworld TCG Soul cards upgrade a deck: what the ESOUL printings do, which upgrade order actually changes games, how gold and parallel Soul printings differ from the base, and how to price an upgrade before you buy it.",
    keywords:
      "palworld tcg soul card upgrades, palworld soul card, palworld gold soul card prices, esoul card list, palworld soul card deck, palworld soul card rarity, palworld soul card upgrade path, palworld tcg soul card value",
    hub: "/tcg-tools",
    hubLabel: "Palworld TCG tools",
    clusterTitle: "Palworld Soul card upgrades",
    question: "How do Soul card upgrades work in the Palworld TCG?",
    answer:
      "Soul cards are the ESOUL-coded printings that sit alongside your Pals rather than replacing them, so an upgrade means swapping one Soul slot for a stronger printing rather than rebuilding the deck. Work out which slot your deck loses games on, check that card's exact printed code on its page at /cards, then price the base printing and its gold or parallel version separately on /prices — the gold version plays identically, so paying the premium is a collecting decision, not a power decision.",
    steps: [
      [
        "Find the slot that loses you games",
        "Track your last ten games and note the turn you fall behind. Upgrade the Soul slot tied to that turn, not the most expensive card on the list.",
      ],
      [
        "Read the exact printed code, not the name",
        "Soul printings use ESOUL codes (for example ESOUL-002). Parallels add a suffix. Two cards with the same name can be two different markets.",
      ],
      [
        "Separate power upgrades from cosmetic ones",
        "A different Soul card is a power change. A gold or parallel version of the same Soul card is the same rules text in a rarer printing — buy it because you want it, not to win more.",
      ],
      [
        "Test the swap before buying the premium printing",
        "Use the deck builder and simulator at /tcg-tools to run the upgraded list first. If the swap does not change your loss pattern, the printing you buy does not matter.",
      ],
      [
        "Price base and parallel side by side",
        "Open /prices and compare the median for the base ESOUL printing against its gold or parallel. The gap is what the cosmetic upgrade actually costs you.",
      ],
      [
        "Sleeve the upgrade the day it arrives",
        "Premium Soul printings show edge wear fast in play. Sleeve on arrival and keep the raw copy for the binder if you intend to resell later.",
      ],
    ],
    data: [
      ["Code prefix", "Soul cards carry ESOUL printed English codes such as ESOUL-002; promo Soul printings run in the same ESOUL range."],
      ["Per-card reference", "Every printed base card has a page at /cards/<CODE> with the official scan, rules text and its parallel printings."],
      ["Where to test a swap", "The deck builder and match simulator at /tcg-tools run the upgraded list before you spend anything."],
      ["Where to price it", "The price chart at /prices for the printing's median and recent sold points; /tcg-price-index for its trend."],
      ["Parallel rule of thumb", "A parallel or gold Soul printing has identical rules text — the difference is scarcity and finish, never power."],
      ["Legality", "Play from the printing list your event uses; a parallel of a legal card is legal, but confirm the format with the organiser."],
    ],
    pitfalls: [
      [
        "Buying the gold printing expecting a stronger card",
        "Gold and parallel Soul printings are cosmetic. If your deck was losing before the upgrade, it loses the same way after it.",
      ],
      [
        "Upgrading the flashiest slot instead of the failing one",
        "Deck upgrades only pay when they fix the turn you lose on. Track your games first, buy second.",
      ],
      [
        "Pricing a Soul parallel off the base card",
        "The suffix is the market. Match the full printed code before you agree to a price in either direction.",
      ],
      [
        "Playing a premium Soul card raw",
        "Unsleeved premium finishes scuff within a session, and that scuff is the difference between two condition tiers at resale.",
      ],
      [
        "Chasing a full Soul set before the deck functions",
        "A complete Soul line-up in a deck with no consistent early game is a collection, not an upgrade. Fix consistency first.",
      ],
    ],
    faq: [
      [
        "What does a Soul card do in the Palworld TCG?",
        "Soul cards are ESOUL-coded printings that support your Pals rather than replacing them, so upgrading means changing which Soul printing occupies a slot. Check the exact card's rules text on its page at /cards before assuming what a swap does.",
      ],
      [
        "Are gold Soul cards better than the normal version?",
        "No. A gold or parallel Soul printing carries identical rules text — only the finish and scarcity differ, so it is a collecting upgrade rather than a power upgrade.",
      ],
      [
        "How much are Palworld gold Soul cards worth?",
        "Prices move with the market, so read the median for the exact ESOUL code on /prices rather than a remembered figure, and compare it against the base printing to see what the finish is costing you.",
      ],
      [
        "Which Soul card should I upgrade first?",
        "The one in the slot where your games actually turn. Log ten games, find the turn you fall behind, and upgrade the Soul card tied to that turn before spending on anything rarer.",
      ],
      [
        "Can I test a Soul card upgrade before buying it?",
        "Yes — build the upgraded list in the deck builder at /tcg-tools and play it out in the simulator. If the loss pattern does not change, the purchase would not have changed it either.",
      ],
      [
        "Are parallel Soul printings tournament legal?",
        "A parallel of a legal card is normally legal because the rules text matches, but formats are set by the organiser, so confirm the accepted printing list before an event.",
      ],
    ],
  },
  {
    slug: "palworld-ocg-vs-tcg-print",
    title: "Palworld OCG vs TCG Print Differences",
    desc:
      "How Japanese-language Palworld Official Card Game stock differs from the Western English print run: card code formats, back and border printing, texture and ink sheen, card thickness and sleeve fit, and how to verify which run a card came from.",
    keywords:
      "palworld ocg vs tcg print differences, palworld japanese vs english cards, palworld card thickness, palworld card texture, palworld card ink sheen, palworld japanese card back, palworld ocg cards, palworld tcg print run differences, palworld card stock quality",
    hub: "/cards",
    hubLabel: "Palworld card database",
    clusterTitle: "Palworld OCG vs TCG print differences",
    question: "What is the difference between Japanese and English Palworld cards?",
    answer:
      "The differences collectors can verify are the printed card code and language on the card face, the finish and texture of the stock, and how the card sits in a sleeve — not a quality ranking. English Western-run cards carry the E-prefixed codes used across this site (EBP01-001, ETD01-012TSP, ESOUL-002), so the fastest way to identify a run is to read the printed code against the card database at /cards, then check texture, sheen and thickness against a known copy from the same run under raking light.",
    steps: [
      [
        "Read the printed code first",
        "The code block on the card face identifies the run before any texture test. English Western printings use the leading-E codes listed at /cards; a code that does not match that pattern is not from the English run.",
      ],
      [
        "Compare the card face language and rules block",
        "Language, name formatting and the rules-text layout are the second identifier and need no equipment. Photograph both cards side by side at the same distance.",
      ],
      [
        "Check texture and ink sheen under raking light",
        "Hold both cards at a shallow angle to a single light source. Differences show up as gloss level and as how the foil catches the light. Judge run-to-run, and only between copies you know the origin of.",
      ],
      [
        "Measure thickness with a caliper, not by feel",
        "Stack five cards from each run and measure the stack, then divide. A single-card measurement sits inside normal tolerance and tells you nothing.",
      ],
      [
        "Test sleeve fit in both directions",
        "Fit matters more than the raw number for play and storage: a card that binds in a sleeve creeps at the corners. Test the sleeve you actually use.",
      ],
      [
        "Record what you measured before you value anything",
        "Note the codes, the light, the sleeve and the stack measurement. Undocumented print-difference claims are treated as guesses by buyers and graders alike.",
      ],
    ],
    data: [
      ["English printed code format", "Leading E plus set and number — EBP01-001 for the base booster set, ETD01-012TSP for trial-deck parallels, ESOUL-002 for Soul printings."],
      ["Where to verify a code", "The card database at /cards, and the per-card page at /cards/<CODE> with the official scan."],
      ["First identifier", "Printed code and card-face language, both readable without tools."],
      ["Texture and sheen method", "Raking light at a shallow angle, one light source, both cards in frame — comparative only, never absolute."],
      ["Thickness method", "Caliper on a five-card stack from each run, divided by five; single-card readings sit inside tolerance."],
      ["Sleeve check", "Insert and remove from the sleeve you use for storage — binding at the corners is the practical difference collectors feel."],
      ["What is not verifiable", "Any claim that one region's stock is 'higher quality' overall; run characteristics vary batch to batch."],
    ],
    pitfalls: [
      [
        "Ranking one region's stock as better quality",
        "Finish characteristics vary between batches inside a single run. A comparison between two cards is evidence about those two cards, not a regional verdict.",
      ],
      [
        "Judging thickness by feel or from one card",
        "Card-stock tolerance is wider than the difference people think they feel. Measure a stack or do not make the claim.",
      ],
      [
        "Buying an import assuming it is the English printing",
        "The printed code settles it. If the listing photo does not show the code block clearly, ask for that photo before paying.",
      ],
      [
        "Mixing runs in one graded submission expectation",
        "Graders label the printing they see. A submission built on your assumption about the run comes back labelled differently and prices differently.",
      ],
      [
        "Using sheen as a counterfeit test",
        "Gloss varies with batch and with handling. Verify against the printed code and the official scan on the card's page instead.",
      ],
    ],
    faq: [
      [
        "How can I tell if a Palworld card is Japanese or English?",
        "Read the printed code block and the card-face language. English Western printings use the leading-E codes catalogued at /cards, such as EBP01-001, so a code outside that pattern is not from the English run.",
      ],
      [
        "Is Japanese Palworld card stock better quality than English?",
        "That is not a verifiable claim. Finish, gloss and thickness vary between batches inside a single run, so any comparison is only evidence about the two specific cards you measured.",
      ],
      [
        "How thick is a Palworld TCG card?",
        "Measure rather than trust a figure: stack five cards from the run with a caliper and divide by five. Single-card readings fall inside normal manufacturing tolerance and cannot separate two runs.",
      ],
      [
        "Do Japanese and English Palworld cards have different textures?",
        "They can differ in gloss and in how foil catches light, which you check by holding both cards at a shallow angle to one light source. Judge comparatively between copies whose origin you already know.",
      ],
      [
        "Can I play imported Palworld cards in a tournament?",
        "Formats and accepted printings are set by the event organiser, so confirm before travelling. Many organisers also require a language the judges can read at the table.",
      ],
      [
        "Do Japanese and English Palworld cards fit the same sleeves?",
        "Test with the sleeve you actually use rather than assuming, because a card that binds at the corners creeps and wears in storage. Fit is the practical difference; the raw thickness number rarely is.",
      ],
    ],
  },
  {
    slug: "palworld-tcg-first-edition",
    title: "Palworld TCG First Edition & Sealed",
    desc:
      "What 'first edition' means for the Palworld TCG: how to identify launch-run Dawn of Palpagos sealed product after the reprint, booster box versus case buying, storage that protects sealed value, and how to price sealed before you commit.",
    keywords:
      "palworld 1st edition booster box, palworld tcg first edition, dawn of palpagos booster box price, dawn of palpagos booster box case price, palworld tcg sealed product, palworld booster box case, palworld sealed investing, palworld tcg first print run",
    hub: "/prices",
    hubLabel: "Palworld TCG price chart",
    clusterTitle: "Palworld first edition & sealed",
    question: "Is there a first edition Palworld TCG booster box?",
    answer:
      "There is a first print run, but no announced first-edition stamp: reprinted Dawn of Palpagos (EBP01) product carries the same card list and the same printed English codes as the launch wave. That makes purchase records, retailer invoices and unopened case seals the only practical way to evidence launch-run stock, so price sealed from live sold data on /prices rather than from a scarcity story.",
    steps: [
      [
        "Understand what you can and cannot prove",
        "With no first-edition stamp announced, provenance is documentation: dated invoices, retailer confirmations and intact case seals. Everything else is a seller's word.",
      ],
      [
        "Decide box or case before you shop",
        "A box is a single opening decision; a sealed case is a storage and capital decision. Cases only make sense if you can keep the outer seal intact and have the space to store it flat and dry.",
      ],
      [
        "Price sealed against completed sales",
        "Open /prices, read the median and the recent sold points for sealed product, and ignore standalone high asks left over from the sold-out window.",
      ],
      [
        "Buy during restock, not during shortage",
        "Confirmed additional print runs mean supply is rising against the same demand. Shortage pricing is the worst entry point for sealed product.",
      ],
      [
        "Store sealed like it is the asset",
        "Cool, dry, dark, upright or flat with no weight on top. Shrink dents, sun fade and crushed corners cut sealed value faster than time does.",
      ],
      [
        "Write the exit down first",
        "Decide the marketplace and the rough window you would sell in before you buy. Sealed positions without an exit get held through every peak.",
      ],
    ],
    data: [
      ["Set", "Booster Box \"Dawn of Palpagos\" (EBP01), the first Palworld Official Card Game set, launched 30 July 2026."],
      ["Reprint status", "Additional print runs were confirmed after the launch wave sold out worldwide; card list and codes are unchanged."],
      ["Box contents", "12 packs per box at 7 cards per pack — 84 cards per box."],
      ["First-run evidence", "Dated purchase records, retailer invoices and intact factory case seals; no first-edition stamp has been announced."],
      ["Sibling products", "Trial decks ETD01 and ETD02 are separate SKUs with their own restock timing."],
      ["Where to price sealed", "The price chart at /prices for medians and sold points; /tcg-price-index for direction of travel."],
      ["Storage baseline", "Cool, dry, dark, no weight on the box, no temperature cycling — lofts, garages and cars fail all four."],
    ],
    pitfalls: [
      [
        "Paying a first-edition premium with no stamp to point at",
        "If no stamp exists, a 'first edition' premium is only as good as the paperwork. Ask for the dated invoice or pay run-agnostic prices.",
      ],
      [
        "Buying sealed at shortage prices",
        "Sold-out-window asks are the ceiling of the market, not its level. Wait for restock and check sold data.",
      ],
      [
        "Opening a case to check the contents",
        "The outer seal is most of a case's premium over loose boxes. Once it is cut, you own boxes.",
      ],
      [
        "Holding sealed EBP01 as a scarcity play",
        "Confirmed reprints remove the scarcity thesis for this set's sealed product. Chase singles, not boxes, carry the scarcity now.",
      ],
      [
        "Storing sealed product in a garage or loft",
        "Humidity and heat cycling warp boxes and cloud shrink wrap, and shrink damage is graded harshly by sealed buyers.",
      ],
    ],
    faq: [
      [
        "Is there a first edition print of the Palworld TCG?",
        "There is a first print run, but no first-edition stamp has been announced, and reprinted Dawn of Palpagos cards keep the same codes and card list. Dated invoices and intact case seals are what evidence launch-run stock.",
      ],
      [
        "How much is a Dawn of Palpagos booster box worth?",
        "It moves with supply, especially around restock waves, so read the median and recent sold points on /prices instead of a remembered figure before you buy or list.",
      ],
      [
        "Is a sealed case better than single booster boxes?",
        "A sealed case keeps its outer seal premium only while it stays intact, and it needs proper storage and idle capital. Boxes are more liquid and easier to store.",
      ],
      [
        "Will the reprint hurt sealed Palworld box values?",
        "Extra print runs raise supply against the same demand, which typically softens sealed pricing while leaving low-pull-rate chase singles firmest.",
      ],
      [
        "How should I store sealed Palworld product?",
        "Cool, dry, dark and stable, with nothing stacked on the shrink wrap. Avoid lofts, garages and cars, where heat and humidity cycling damage boxes far faster than age does.",
      ],
      [
        "How many packs are in a Palworld booster box?",
        "Twelve packs of seven cards, so 84 cards per box — that is the denominator to use for any pull-rate or per-card cost calculation.",
      ],
    ],
  },
  {
    slug: "palworld-card-price-tracking",
    title: "Palworld Card Price Tracking",
    desc:
      "How to track Palworld TCG card prices properly: reading the median instead of asking prices, using the Pal Stars price index and per-card charts, understanding liquidity, and setting up a repeatable weekly check.",
    keywords:
      "palworld card price tracker, palworld tcg price index, palworld card prices, palworld tcg market tracker, palworld card value checker, palworld tcg price history, palworld card singles buy online, palworld tcg price chart, palworld tcg graded cards",
    hub: "/tcg-price-index",
    hubLabel: "Palworld TCG Price Index",
    clusterTitle: "Palworld card price tracking",
    question: "How do you track Palworld TCG card prices?",
    answer:
      "Track medians and trends, not individual listings: open the Pal Stars TCG Price Index at /tcg-price-index for the market as a whole, then the per-card charts at /prices for the exact printed code you care about. Both read from the same marketplace sync, so a card's chart and its index row agree, and each card also shows a liquidity score so you can tell a real market from two listings and no sales.",
    steps: [
      [
        "Start from the index, not a single card",
        "The index at /tcg-price-index tells you whether the whole market is rising or cooling. A single card moving against the index usually means card-specific news, not a trend.",
      ],
      [
        "Search by exact printed code",
        "Type the full code (EBP01-001, ETD01-012TSP, ESOUL-002). Parallels have their own markets, and searching by Pal name mixes them together.",
      ],
      [
        "Read the median, then the sold points",
        "The median across marketplaces is the market; asking prices are one seller's hope. The chart marks recent peaks and troughs so you can see where the median came from.",
      ],
      [
        "Check the liquidity score before acting",
        "A high price on a thin market is not a price you can realise. Liquidity tells you whether the card actually sells at that level.",
      ],
      [
        "Set a fixed weekly check",
        "Same day, same two pages, same codes. Consistency is what turns price data into a decision; dipping in during hype does the opposite.",
      ],
      [
        "Log your own buys and sells",
        "The collection tracker at /collection records what you paid and what you sold for, which is the only price history that reflects your results.",
      ],
    ],
    data: [
      ["Market-level view", "The Pal Stars TCG Price Index at /tcg-price-index, an equal-weight benchmark with a market-cap-weighted series alongside it."],
      ["Per-card view", "The price chart at /prices, showing the median across marketplaces plus recent peak and trough points per printed code."],
      ["Data source", "Marketplace and TCGplayer feeds synced on a fixed daily schedule; every page shows the real last-sync time."],
      ["Code format", "Exact printed English codes with the leading E; parallels add a suffix and price separately."],
      ["Liquidity score", "A 0-100 score per card showing how tradable it is, so a high headline price on a thin market is visible as such."],
      ["Your own history", "The collection tracker at /collection stores your buy price, sale price and profit per card."],
      ["Machine-readable feed", "A public card price JSON feed is available for anyone citing or charting the index."],
    ],
    pitfalls: [
      [
        "Valuing a card from one listing",
        "Any seller can list any number. Use the median of completed sales, which is what the chart is built from.",
      ],
      [
        "Searching by Pal name instead of code",
        "Names span sets and parallels. The code is the only identifier that maps to one market.",
      ],
      [
        "Ignoring liquidity on a high-priced card",
        "A price nobody transacts at is not a price. Check the liquidity score before you treat a number as your exit.",
      ],
      [
        "Checking prices only when something is hyped",
        "Hype windows are the worst sample of a market. A fixed weekly check gives you the direction hype hides.",
      ],
      [
        "Comparing a raw price to a graded price",
        "Graded and raw are separate markets with separate buyers. Compare like to like or the gap will look like profit that is not there.",
      ],
    ],
    faq: [
      [
        "Is there a Palworld TCG price tracker?",
        "Yes — the Pal Stars price chart at /prices tracks the median and recent sold points per printed card code, and the TCG Price Index at /tcg-price-index tracks the market as a whole. Both read from the same daily marketplace sync.",
      ],
      [
        "How often do Palworld card prices update?",
        "Prices sync on a fixed daily schedule, and every price surface shows the real last-sync timestamp rather than the time the page was built, so you can always see how current a number is.",
      ],
      [
        "What is the Palworld TCG Price Index?",
        "It is an equal-weight benchmark that tracks a basket of Palworld printings against a base value, with a market-cap-weighted series alongside it, so you can see market direction without reading dozens of individual cards.",
      ],
      [
        "How do I check what one Palworld card is worth?",
        "Search the exact printed code on /prices and read the median across marketplaces plus the recent sold points, then check the liquidity score to confirm the card actually trades at that level.",
      ],
      [
        "Where can I buy Palworld card singles?",
        "Each card page links out to current marketplace listings for that exact printing, so you buy the code you priced rather than a different parallel with the same name.",
      ],
      [
        "Can I track my own Palworld collection value?",
        "Yes — the collection tracker at /collection records your buy price, sale price and running profit per card, which is the only price history that reflects what you actually paid.",
      ],
    ],
  },
  {
    slug: "palworld-tcg-promo-card-values",
    title: "Palworld TCG Promo Card Values",
    desc:
      "How to value 2026 Palworld TCG promo cards: what the EPR and ESOUL promo codes cover, how event and insert distribution creates scarcity, why Not-For-Sale printings need extra provenance, and how to price a promo before you buy.",
    keywords:
      "palworld tcg 2026 pr cards value, palworld promo card value, palworld epr cards, palworld not for sale card, palworld tcg promo cards, palworld tcg event exclusive card, palworld trial deck exclusive cards, palworld tcg osr prices",
    hub: "/cards",
    hubLabel: "Palworld card database",
    clusterTitle: "Palworld promo card values",
    question: "What are Palworld TCG promo cards worth?",
    answer:
      "Promo value comes from distribution, not artwork: an event-only or product-insert printing exists in smaller numbers than a base set card at the same rarity, which is the structural reason it can hold a premium. Identify the exact promo code on its page at /cards, confirm how it was distributed, then read the median on /prices — and for Not-For-Sale printings, expect buyers to ask for provenance before they pay.",
    steps: [
      [
        "Identify the promo code exactly",
        "2026 promos use EPR codes, with Soul promos in the ESOUL range. The code, not the Pal, determines the market.",
      ],
      [
        "Establish how it was distributed",
        "Event prize, product insert, trial-deck exclusive or Not-For-Sale demo copy — each has a different pool of copies and therefore a different market.",
      ],
      [
        "Check it against the official scan",
        "Compare the card in hand to the official scan on its /cards page. Promo layouts differ from base printings, which is exactly what counterfeits get wrong.",
      ],
      [
        "Read the median before you negotiate",
        "Open /prices for that code. Promos trade thinly, so also read the liquidity score: a single high ask on a thin market is not a value.",
      ],
      [
        "Keep provenance for Not-For-Sale printings",
        "Demo and prototype copies were never sold at retail, so buyers reasonably ask where it came from. Event photos, tournament records and prior sale records carry the sale.",
      ],
      [
        "Sleeve and store promos as collection pieces",
        "Most promos are not deck cards. Sleeve on arrival, store upright in side-loading pages, and keep them out of playtesting.",
      ],
    ],
    data: [
      ["2026 promo codes", "Promo printings use EPR codes; promo Soul printings sit in the ESOUL range."],
      ["Per-card reference", "Every promo has a page at /cards/<CODE> with the official scan and its printing details."],
      ["Scarcity driver", "Distribution channel — event prize, product insert, trial-deck exclusive or Not-For-Sale demo — not artwork or Pal popularity."],
      ["Not-For-Sale printings", "The PR2026 EPR and ESOUL demo printings are the site's only Not-For-Sale cards; they were never retail product."],
      ["Trial-deck exclusives", "Cards printed only in ETD01 and ETD02 carry their own codes and prices separately from the booster set."],
      ["Where to price", "The chart at /prices for the median and sold points, with the liquidity score for how thin the market is."],
      ["Counterfeit check", "Compare against the official scan on the card page; promo layouts differ from base printings in ways copies get wrong."],
    ],
    pitfalls: [
      [
        "Valuing a promo off the base card",
        "A promo and its base printing share a name and nothing else. Match the printed code before you name a price.",
      ],
      [
        "Buying a Not-For-Sale card with no provenance",
        "Demo and prototype printings were never retail. With no history attached, you are buying a story at a premium.",
      ],
      [
        "Treating a thin market as a firm price",
        "Promos trade rarely. Check liquidity before you assume a high number is achievable when you sell.",
      ],
      [
        "Assuming every promo is scarce",
        "Widely inserted promos can be more common than mid-rarity set cards. Distribution decides scarcity, and it is checkable.",
      ],
      [
        "Playing promos in tested decks",
        "Play wear on a thin-market card is expensive relative to its value. Keep a normal copy for play.",
      ],
    ],
    faq: [
      [
        "How much are Palworld TCG promo cards worth?",
        "It depends on distribution rather than artwork: event-only and insert printings exist in smaller numbers, so they can hold a premium. Read the median for the exact EPR or ESOUL code on /prices, and check liquidity because promos trade thinly.",
      ],
      [
        "What are EPR cards in the Palworld TCG?",
        "EPR is the code prefix used by 2026 promo printings, with promo Soul cards in the ESOUL range. Each has its own page at /cards showing the official scan and printing details.",
      ],
      [
        "What does Not For Sale mean on a Palworld card?",
        "It marks a demo or prototype printing that was never sold at retail. Those copies can change hands between collectors, but buyers reasonably expect provenance before paying a premium.",
      ],
      [
        "Are trial deck exclusive cards valuable?",
        "They can be, because a card printed only in ETD01 or ETD02 has a smaller pool than the booster set. Price the exclusive's own code rather than a similar booster printing.",
      ],
      [
        "How do I spot a fake Palworld promo card?",
        "Compare the card in hand to the official scan on its /cards page, checking the code block, layout and rarity treatment. Promo layouts differ from base printings, and that is where copies fail.",
      ],
      [
        "Should I grade a Palworld promo card?",
        "Grade it when the raw copy already carries a premium and a high grade would reach a different buyer pool. On a widely inserted promo the fee will exceed the uplift.",
      ],
    ],
  },
];

const POOL = OFFICIAL_LINKS;
function pool(offset: number) {
  return [POOL[offset % POOL.length], POOL[(offset + 1) % POOL.length], POOL[(offset + 2) % POOL.length]];
}

function articles(t: Topic): GuideArticle[] {
  const hubSentence = `Everything here pairs with the ${t.hubLabel} at ${t.hub}.`;
  return [
    {
      title: t.title,
      slug: "overview",
      intro: `${t.answer} ${hubSentence}`,
      sections: [
        { heading: t.question, text: t.answer },
        { heading: "Why this matters", text: `${t.steps[0][1]} ${t.pitfalls[0][1]}` },
        { heading: "What is actually checkable", text: t.data.map(([k, v]) => `${k}: ${v}`).join(" ") },
        {
          heading: `Use it with the ${t.hubLabel}`,
          text: `${hubSentence} Work from the live data there rather than a remembered price or a seller's description.`,
        },
      ],
      externalLinks: pool(1),
    },
    {
      title: `${t.title}: step-by-step`,
      slug: "step-by-step",
      intro: "The order to work through, with the mistake each step removes.",
      sections: t.steps.map(([heading, text], i) => ({ heading: `${i + 1}. ${heading}`, text })),
      externalLinks: pool(0),
    },
    {
      title: `${t.title}: reference table`,
      slug: "reference",
      intro: `Confirmed details behind ${t.title.toLowerCase()}, in the order you will need them.`,
      sections: t.data.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(2),
    },
    {
      title: `${t.title}: mistakes and fixes`,
      slug: "mistakes",
      intro: "The failure modes that cost collectors the most money here, each with the fix.",
      sections: t.pitfalls.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(3),
    },
    {
      title: `${t.title}: FAQ`,
      slug: "faq",
      intro: `Direct answers to the questions people ask about ${t.title.toLowerCase()}.`,
      sections: t.faq.map(([q, a]) => ({ heading: q, text: a })),
      externalLinks: pool(4),
    },
  ];
}

export const INVESTING_GUIDES_2026: Guide[] = TOPICS.map((t) => ({
  slug: t.slug,
  title: `${t.title} & FAQ`,
  desc: t.desc,
  tag: "TCG",
  updated: "2026-09-04",
  keywords: t.keywords,
  articles: articles(t),
}));

/** Hub page each investing cluster supports, for internal linking. */
export const INVESTING_CLUSTERS_2026: Record<string, { slug: string; title: string }[]> = TOPICS.reduce(
  (acc, t) => {
    (acc[t.hub] ??= []).push({ slug: t.slug, title: t.clusterTitle });
    return acc;
  },
  {} as Record<string, { slug: string; title: string }[]>,
);
